import { gaService } from "@coworker/common/services/google-analytics.service";

let lastScanned, lastFound, scannerLastCode;
let hashResolve = null;

function createHashPromise() {
  return new Promise((resolve) => {
    hashResolve = resolve;
  });
}

const hashPromise = createHashPromise();

export function trackSwapRandomTestBuyArticle(
  typeOfTestBuy,
  currentItemShortId,
  hfb
) {
  return logEvent("ce:swap_random_testbuy_article", {
    typeOfTestBuy,
    currentItemShortId,
    hfb,
  });
}
export function trackSwapScannedTestBuyArticle(
  typeOfTestBuy,
  currentItemShortId,
  hfb
) {
  return logEvent("ce:swap_scanned_testbuy_article", {
    typeOfTestBuy,
    currentItemShortId,
    hfb,
  });
}

export async function setUserId(userId, hashedUid) {
  let hashedUserId;
  if (import.meta.env.VITE_APP_ENV !== "ci") {
    hashedUserId = hashedUid;
    await gaService.setGAUserId(hashedUserId);
    return hashResolve();
  }
}

export function setUserStore(store) {
  return gaService.setGAUserProperties({ store: `store-${store}` });
}

export function setStartedAtVersion(started_at_version) {
  return gaService.setGAUserProperties({ started_at_version });
}

export function setCountry(country) {
  return gaService.setGAUserProperties({ country });
}

/**
 * Wrapper function that checks if the analytics object exists and then logs the
 * event.
 *
 * @param {String} eventName
 * @param {Object} eventParams
 */
function logEvent(eventName, eventParams = {}) {
  if (window.debugLogEvent) console.log("GA logEvent", eventName, eventParams);
  return hashPromise.then(() => {
    gaService.logEvent(eventName, eventParams);
  });
}

const trackerHelper = {
  trackLogin() {
    try {
      const tasks = {};
      tasks["select_issue"] = "Product_issue";
      if (typeof navigator !== "undefined" && navigator.userAgent) {
        if (navigator.userAgent.includes("Co-worker Hub")) {
          logEvent("ce:login", { login_origin: "cwh" });
        } else {
          logEvent("ce:login", { login_origin: "app" });
        }
      } else {
        console.warn("User agent not available");
      }
    } catch (e) {
      console.error("Error in trackLogin", e);
    }
  },
  trackNewScanner(active) {
    logEvent("ce:new_scanner", { active });
  },
  trackHoneywellScannerUsed() {
    logEvent("ce:honeywell_scanner_used");
  },
  trackNewCameraScannerUsed() {
    logEvent("ce:new_camera_scanner_used");
  },
  trackCamera(duration, code) {
    // This typically logs 2+ events per actual camera scan, and we should instead use ce:barcode:scan
    logEvent("ce:scan_barcode", { duration });

    if (lastScanned === code) return;
    logEvent("ce:barcode:scan", { duration });
    lastScanned = code;
  },

  trackSearch: (duration) => logEvent("ce:scan_search", { duration }),

  trackCreateTask(task) {
    const {
      task_type,
      custom_title,
      store_id,
      issue_type,
      priority_flag,
      task_id,
      state,
      assigned_status,
    } = task;

    logEvent("ce:create_task", {
      task_type,
      custom_title,
      store_id,
      issue_type,
      priority_flag,
      task_id,
      state,
      assigned_status,
    });
  },

  trackCreateAdditionalTestBuy(response, testBuyId) {
    logEvent("ce:create_additional_test_buy", {
      response,
      testBuyId,
    });
  },

  trackMaxAmountExceeded(data) {
    const { warningShown, popupShown, popupSelection } = data;

    logEvent("ce:max_amount_exceeded", {
      warning_shown: warningShown,
      popup_shown: popupShown,
      popup_selection: popupSelection,
    });
  },

  trackPickUpTask({ time_to_pickup, time_from_pickup, task_id }) {
    logEvent("ce:pickup_task", { time_to_pickup, time_from_pickup, task_id });
  },

  trackPickAddonTask(
    data,
    with_changes = false,
    location_change = false,
    quantity_change = false
  ) {
    const { task_id, requested_quantity, pick_quantity } = data;
    logEvent("ce:pick_addon_task", {
      task_id,
      requested_quantity,
      pick_quantity,
      with_changes,
      location_change,
      quantity_change,
    });
  },

  trackRefillAddonTask({ task_id }, with_changes = false) {
    logEvent("ce:refill_addon_task", {
      task_id,
      with_changes,
    });
  },

  trackNotificationCTAClick(event_type = false) {
    logEvent("ce:push_notification_cta_click", { event_type });
  },

  trackCompleteTask(
    task,
    {
      with_changes = false,
      create_and_complete = false,
      pick_and_refill = false,
    }
  ) {
    const {
      task_type,
      custom_title,
      store_id,
      issue_type,
      priority_flag,
      task_id,
      requested_quantity,
      refilled_quantity,
      pick_quantity,
    } = task;

    logEvent("ce:complete_task", {
      task_type,
      custom_title,
      store_id,
      issue_type,
      priority_flag,
      with_changes,
      task_id,
      create_and_complete,
      pick_and_refill,
      requested_quantity,
      refilled_quantity,
      pick_quantity,
    });
  },

  trackCloseTask(task, issue) {
    const {
      task_type,
      custom_title,
      store_id,
      issue_type,
      priority_flag,
      picked,
      task_id,
    } = task;

    logEvent("ce:close_task", {
      task_type,
      custom_title,
      store_id,
      issue_type,
      priority_flag,
      picked,
      task_id,
      issue,
    });
  },

  trackDropTask(task, issue) {
    const {
      task_type,
      custom_title,
      store_id,
      issue_type,
      priority_flag,
      picked,
      task_id,
    } = task;

    logEvent("ce:drop_task", {
      task_type,
      custom_title,
      store_id,
      issue_type,
      priority_flag,
      picked,
      task_id,
      issue,
    });
  },

  trackPickupLocationChange: () => logEvent("ce:pickup_location_changed"),
  trackReassignTask: (id) => logEvent("ce:task_reassigned", { id }),
  trackHandoffTask: (id, user, group) =>
    logEvent("ce:task_handoff", { id, user, group }),

  trackPIPEntered: () => logEvent("ce:pip_entered"),

  trackUndo: (undone_action) => logEvent("ce:undo", { undone_action }),
  trackDismissModal: (usingX) => logEvent("ce:dismiss_modal", { usingX }),

  task: {
    trackMarkAsCompleteToggle(state) {
      logEvent("ce:mark_as_complete_toggled", { state });
    },
    trackCompletePickingOnlyToggle(state) {
      logEvent("ce:complete_picking_only_toggled", { state });
    },
    trackAssignToMyTeamOpenedToggle(state) {
      logEvent("ce:assign_to_my_team_foldout_toggled", { state });
    },
  },
  taskSwipeButtons: {
    trackComplete: () => logEvent("ce:taskSwipeButtons:complete"),
    trackPickup: () => logEvent("ce:taskSwipeButtons:pickup"),
  },

  changeTeam: {
    confirm: (value) => logEvent("ce:changeTeam:confirm", { value }),
    viewTeamList: () => logEvent("ce:changeTeam:viewTeamList"),
  },

  plannedTasks: {
    openPopup: () => logEvent("ce:plannedTasks:openPopup"),
    created(planned) {
      logEvent("ce:plannedTasks:created", {
        recurring: planned.recurring,
        daysOfWeek: planned.days_of_week?.length ?? 0,
        everyNWeeks: planned.every_n_weeks ?? 0,
      });
    },
    submitted(planned) {
      logEvent("ce:plannedTasks:submitted", {
        recurring: planned.recurring,
        daysOfWeek: planned.days_of_week?.length ?? 0,
        everyNWeeks: planned.every_n_weeks ?? 0,
      });
    },
    linkToParent: () => logEvent("ce:plannedTasks:detail:linkToParent"),
    formClickX: () => logEvent("ce:plannedTasks:form:clickX"),
  },

  barcode: {
    logErrorUseStream(e) {
      logEvent("ce:barcode:error:usestream", {
        message: e.message,
        stack: e.stack,
      });
    },
    logErrorTrackTorch(e) {
      logEvent("ce:barcode:error:tracktorch", {
        message: e.message,
        stack: e.stack,
      });
    },
    logErrorVideoPlay(e) {
      logEvent("ce:barcode:debug:errorvideoplay", {
        message: e.message,
        stack: e.stack,
      });
    },

    logRDTCaptured(code, isValid) {
      if (!code || lastFound === code) return;
      logEvent("ce:barcode:debug:rdt_captured", { code, isValid });
      lastFound = code;
    },

    // This gets called on capture of any barcode, but ONLY if flag BARCODE_DEBUG is enabled.
    logCaptured(code, isValid) {
      if (!code || lastFound === code) return;
      logEvent("ce:barcode:debug:captured", { code, isValid });
      lastFound = code;
    },

    logScannerType(type, code) {
      if (!code || scannerLastCode === code) return;
      logEvent("ce:scanner:type", { type });
      scannerLastCode = code;
    },

    logZoomUsage(multiplier) {
      logEvent("ce:zoom_slider", { multiplier });
    },
  },

  safetyAlarmInfo: {
    open: () => logEvent("ce:safety_alarm_info:open"),
    closeX: () => logEvent("ce:safety_alarm_info:close_x"),
    closeOK: () => logEvent("ce:safety_alarm_info:close_ok"),
  },

  multipleSelect: {
    logCancel(selectedTaskCount) {
      logEvent("ce:multiple_select:stop_cancel_click", { selectedTaskCount });
    },
    logProcessed(selectedTaskCount, failedCount, action_type) {
      logEvent("ce:multiple_select:tasks_processed", {
        selectedTaskCount,
        failedCount,
        action_type,
      });
    },
    logMultiselectIconClicked() {
      logEvent("ce:multiple_select:start_icon_click");
    },
  },

  UserFilters: {
    trackFilterChange(store_id, filters, nextFilters) {
      logEvent("ce:mfaq:filter_change", { store_id, filters, nextFilters });
    },
  },

  MFAQ: {
    trackCreateQuestion(question) {
      const { store_id, type, area, question_text, subject } = question;
      logEvent("ce:mfaq:create_question", {
        store_id,
        type,
        area,
        question_text,
        subject,
      });
    },
    trackVote(question_id, store_id, value, vote_location) {
      logEvent("ce:mfaq:track_vote", {
        question_id,
        store_id,
        value,
        vote_location,
      });
    },
    trackTaskResolutionWidget(question_text, question_area) {
      logEvent("ce:mfaq:resolution_widget_clicked", {
        question_text,
        question_area,
      });
    },
    trackMergeButton(firstQuestionId, secondQuestionId) {
      logEvent("ce:mfaq:merge_button", {
        firstQuestionId,
        secondQuestionId,
      });
    },
    trackMergeNotPossible(firstQuestionId, secondQuestionId) {
      logEvent("ce:mfaq:merge_not_possible", {
        firstQuestionId,
        secondQuestionId,
      });
    },
  },

  taskEfficiency: {
    trackTeamChange() {
      logEvent("ce:task_efficiency:team_change");
    },
    trackNearbyAddonAdded() {
      logEvent("ce:task_efficiency:nearby_addon_added");
    },
    trackWarnDuplicate() {
      logEvent("ce:task_efficiency:warn_duplicate");
    },
    trackWarnOutOfStock() {
      logEvent("ce:task_efficiency:warn_out_of_stock");
    },
    trackCreateOutOfStockProductIssue() {
      logEvent("ce:task_efficiency:create_out_of_stock_product_issue");
    },
  },

  livlig: {
    trackLinkDisplay(store_id, product_id, feature) {
      logEvent("ce:livlig:track_link_display", {
        store_id,
        product_id,
        feature,
      });
    },
    trackLinkClick(store_id, product_id, feature) {
      logEvent("ce:livlig:track_link_click", {
        store_id,
        product_id,
        feature,
      });
    },
  },

  trackGlobalActionClick: (actionName) => {
    logEvent("ce:global_action:clicked", { action_name: actionName });
  },

  trackCreateTaskInEmptyState: () => {
    logEvent("ce:task_preview_createtask_button:clicked");
  },

  trackMyOpenTasksPreview: (tabName) => {
    logEvent("ce:my_open_tasks_card_preview:clicked", {
      section: tabName,
      is_from_home_page: true,
    });
  },

  trackOpenTasksPreview: (tabName) => {
    logEvent("ce:open_tasks_card_preview:clicked", {
      section: tabName,
      is_from_home_page: true,
    });
  },

  trackQuickActionsButton: (type) => {
    logEvent(`ce:quick_actions:open`, { type: type });
  },

  trackTaskCardPreview: (tabName) => {
    logEvent("ce:task_card_preview:clicked", { section: tabName });
  },

  trackTaskDetailActionButtons: (buttonName) => {
    logEvent("ce:task_detail_page_swipe_action_buttons:open", {
      button_name: buttonName,
    });
  },

  trackTaskCompletion: (buttonName, hasChanges) => {
    logEvent("ce:complete_task_button:clicked", {
      button_name: buttonName,
      has_changes: hasChanges,
    });
  },

  trackMultiSelectButtonTab: (tabName) => {
    logEvent("ce:multi_select_button:clicked", {
      clicked_from_tab: tabName,
    });
  },

  trackProfileButtonClick: () => {
    logEvent("ce:profile_button:clicked");
  },

  trackSearchButtonClick: () => {
    logEvent("ce:search_button:clicked");
  },

  trackButtomNavigationClick: (buttonName) => {
    logEvent("ce:bottom_navigation:clicked", {
      button_name: buttonName,
    });
  },

  trackMainHeaderNavigationLinksClick: (linkName) => {
    logEvent("ce:main_header_navigation_links:clicked", {
      link_name: linkName,
    });
  },

  trackNamingConventionPopupButton: () => {
    logEvent("ce:naming_convention_popup_button:clicked");
  },

  trackAddCustomLocationButtonClick: () => {
    logEvent("ce:add_custom_location_button:clicked");
  },

  trackCutomLocationItemClick: (locationName) => {
    logEvent("ce:custom_location_item:clicked", {
      location_name: locationName,
    });
  },

  trackSLMLocationItemClick: (locationName) => {
    logEvent("ce:SLM_location_item:clicked", {
      location_name: locationName,
    });
  },

  trackHejComponentSquareGridIconClick: () => {
    logEvent("ce:hej_component_square_grid_icon");
  },

  trackHejComponentEvent: (eventName, params) => {
    // notice that it's not ce: as Hej can pass any event through here
    logEvent("hej_app_switcher_event", {
      custom_title: eventName,
      link: params[0]?.url ? params[0].url : "",
      state: params[1],
    });
  },

  trackActivityTabClick: (tab) => {
    logEvent("ce:activity_tab:clicked", {
      type: tab,
    });
  },

  trackActivityTaskCardClick: () => {
    logEvent("ce:activity_task_card:clicked");
  },

  trackDiscoverNowClick: () => {
    logEvent("ce:discover_now:clicked");
  },

  trackUpptackaSetUpHelpClick: () => {
    logEvent("ce:upptacka_set_up_help:clicked");
  },

  trackUpptackaCreateTaskClick: () => {
    logEvent("ce:upptacka_create_task:clicked");
  },

  trackStoreSwitcher: (value) => {
    logEvent("ce:store_switcher:clicked", {
      from: value,
    });
  },

  trackStoreSwitcherTasksOption: (option) => {
    logEvent("ce:store_switcher_tasks_option:clicked", {
      option: option,
    });
  },

  trackMultiOrderAddonsCreated: ({
    items,
    addons,
    storeId,
    refillAreaId,
    markAsCompleted,
    markAsPriority,
  }) => {
    logEvent("ce:create_multi_order_addons:clicked", {
      refill_area_id: refillAreaId,
      number_of_articles: items?.length,
      number_of_addons_created: addons?.length,
      number_of_addons_with_changed_pieces: addons?.filter(
        (addon) => parseInt(addon.selectedPieces) !== addon.lastRefilledQuantity
      )?.length,
      store_id: storeId,
      mark_as_completed: markAsCompleted,
      mark_as_priority: markAsPriority,
    });
  },

  trackTaskLinkClickedInMenu: () => {
    logEvent("ce:task_link_menu:clicked");
  },

  filter: {
    /**
     * @param {string} feature
     * @param {{filterId: string, value: unknown}[]} values
     */
    trackFilterValues: (feature, values) => {
      // Track taskType and locations for tasks
      if (feature === "tasks") {
        const taskType = values.find(({ filterId }) => filterId === "taskType");
        if (taskType) {
          const logObject = {
            feature,
            task_type: taskType.value,
          };
          const locations = values.find(
            ({ filterId }) => filterId === "locations"
          );
          if (locations) {
            logObject.locations = locations.value.sort().join(",");
          }
          const pickupLocations = values.find(
            ({ filterId }) => filterId === "pickupLocations"
          );
          if (pickupLocations) {
            logObject.pickup_locations = pickupLocations.value.sort().join(",");
          }
          const refillLocations = values.find(
            ({ filterId }) => filterId === "refillLocations"
          );
          if (refillLocations) {
            logObject.refill_locations = refillLocations.value.sort().join(",");
          }
          logEvent("ce:filtervalue", logObject);
        }
      } else {
        logEvent("ce:filtervalue:feature", { feature });
      }
    },
    /**
     * @param {string} feature
     * @param {"mobile"|"desktop"} mode
     * @param {"open_modal_button"|
     *         "reset_button"|
     *         "modal_reset_button"|
     *         "modal_apply_button" } name
     */
    trackFilterGeneralClicked: (feature, mode, name) => {
      logEvent(`ce:filter:${name}:clicked`, {
        feature,
        mode,
      });
    },
    /**
     * @param {string} feature
     * @param {"mobile"|"desktop"} mode
     * @param {string} filterId
     */
    trackFilterRowClicked: (feature, mode, filter_id) => {
      logEvent("ce:filter_row:clicked", {
        feature,
        mode,
        filter_id,
      });
    },
  },
};

export default trackerHelper;
export { logEvent };
